import React, { useEffect, useState } from "react";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Skeleton } from "../ui/skeleton";
import { TestRunService } from "../../services/TestRunService";
import { TestRunRequest } from "../../model_gen/evaluation";
import TestRunRequestComparisonTable from "./TestRunRequestComparisonTable";

interface TestRunRequestComparisonProps {
  testRunId: number;
  testRunRequestId: number;
  isVisible: boolean;
  onClose: () => void;
  apiKey: string;
  testSetId?: string;
  systemPrompt?: string;
  intentGroupId?: string;
}

const TestRunRequestComparison: React.FC<TestRunRequestComparisonProps> = ({
  testRunId,
  testRunRequestId,
  isVisible,
  onClose,
  apiKey,
  testSetId,
  systemPrompt,
  intentGroupId,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [comparisonData, setComparisonData] = useState<TestRunRequest[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<string, TestRunRequest[]>
  >({});

  useEffect(() => {
    if (isVisible && testRunId && testRunRequestId) {
      const cacheKey = `${currentPage}-${pageSize}`;
      if (requestsByPage[cacheKey]) {
        setComparisonData(requestsByPage[cacheKey]);
      } else {
        fetchRequests(currentPage, pageSize);
      }
    }
  }, [isVisible, testRunId, testRunRequestId, currentPage, pageSize]);

  const fetchRequests = (page: number, size: number) => {
    const offset = (page - 1) * size;
    setIsLoading(true);
    TestRunService.getTestRunRequestComparison(
      testRunId,
      testRunRequestId,
      offset,
      size,
      setIsLoading,
      apiKey
    ).subscribe(
      (response: { items: TestRunRequest[]; total: number }) => {
        setComparisonData(response.items);
        setTotalRequests(response.total);
        const cacheKey = `${page}-${size}`;
        setRequestsByPage((prev) => ({
          ...prev,
          [cacheKey]: response.items,
        }));
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching comparison data:", error);
        setIsLoading(false);
      }
    );
  };

  const handlePageChange = (page: number, size?: number) => {
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
      setRequestsByPage({}); // Clear cache when page size changes
    } else {
      setCurrentPage(page);
    }
  };



  return (
    <Dialog open={isVisible} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl w-full max-h-[90vh] bg-background border-border" style={{ overflow: 'visible' }}>
        <DialogHeader className="pb-4 border-b border-border">
          <DialogTitle className="text-xl font-gc-med text-foreground">
            Test Run Request Comparison
          </DialogTitle>
          <p className="text-sm text-muted-foreground font-gc-reg mt-2">
            Comparing responses across different test runs for the same test request
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-visible">
          {isLoading ? (
            <div className="space-y-4 p-6">
              {/* Loading skeleton that matches the table structure */}
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
              
              {/* Table header skeleton */}
              <div className="border border-border rounded-lg">
                <div className="grid grid-cols-5 gap-4 p-4 border-b border-border/50">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-16" />
                </div>
                
                {/* Table rows skeleton */}
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="grid grid-cols-5 gap-4 p-4 border-b border-border/50">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-6 w-12 rounded-full" />
                    <Skeleton className="h-4 w-20" />
                    <div className="flex gap-2">
                      <Skeleton className="h-8 w-16 rounded" />
                      <Skeleton className="h-8 w-20 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 pb-6">
              <TestRunRequestComparisonTable
                data={comparisonData}
                loading={false}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                totalItems={totalRequests}
                apiKey={apiKey}
                testSetId={testSetId}
                systemPrompt={systemPrompt}
                intentGroupId={intentGroupId}
                testRunId={testRunId}
                testRunRequestId={testRunRequestId}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TestRunRequestComparison;
